import { type FC, useCallback } from "react";
import { Text } from "@stubhub/broadway-web";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { cellRecipe, gaWeekendLabelStyle, rowStyle } from "./styles";

export interface WeekendInfo {
  key: string;
  label: string;
  dateRange?: string;
}

interface Props {
  weekends: readonly WeekendInfo[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  /** Accessible label for the group, sourced from CMS content. */
  ariaLabel: string;
}

export const GAWeekendSelector: FC<Props> = ({ weekends, selectedKey, onSelect, ariaLabel }) => {
  const storePageEvent = useStorePageEvent();

  const handleSelect = useCallback(
    (key: string | null) => {
      storePageEvent(
        PE_TYPE.CLICK,
        PE_KEYS.GA_WEEKEND_TOGGLE_CLICK,
        JSON.stringify({ selected: key, previous: selectedKey }),
      );
      onSelect(key);
    },
    [onSelect, selectedKey, storePageEvent],
  );

  if (weekends.length <= 1) return null;

  return (
    <div className={rowStyle} role="group" aria-label={ariaLabel}>
      {weekends.map((w) => {
        const isSelected = w.key === selectedKey;
        return (
          <button
            key={w.key}
            type="button"
            className={cellRecipe({ selection: isSelected ? "selected" : "unselected" })}
            onClick={() => handleSelect(isSelected ? null : w.key)}
            aria-pressed={isSelected}
          >
            <span className={gaWeekendLabelStyle}>{w.label}</span>
            {w.dateRange ? (
              <Text variant="label" size="extra-small" color="text">
                {w.dateRange}
              </Text>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};
